import { useTranslation } from 'react-i18next';
import { Button, Field, InfoBar, Panel, Select } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { cn } from '@/shared/lib/utils';
import { useGetProductsStationFormOptions } from '@/api/generated/products-stations/products-stations';
import { DestinationSelectorField } from '../../../components/DestinationSelector';
import { StationTypeSelectorField } from '../../../components/StationTypeSelector';
import type { StationFormApi } from '../useStationForm';

const LABEL_W = 104;

export interface StationIdentityPanelProps {
  form: StationFormApi;
  title: string;
  /** Spustí mapový režim „Nastavit souřadnice stanice" (jen pro stanici bez nástupních míst). */
  onSetCoordsFromMap: () => void;
}

/** Sbalitelný panel identity stanice — název, kód, typ, druh, destinace, časový posun + souřadnice. */
export function StationIdentityPanel({
  form,
  title,
  onSetCoordsFromMap,
}: StationIdentityPanelProps) {
  const { t } = useTranslation('products');
  const optionsQuery = useGetProductsStationFormOptions();
  const timeZones = optionsQuery.data?.timeZones ?? [];

  return (
    <Panel
      variant="filled"
      icon="building"
      collapsible
      title={title}
      subtitle={
        <form.Subscribe selector={(state) => state.values.code}>
          {(code) => (code ? <span className="text-phx-sm font-mono">{code}</span> : null)}
        </form.Subscribe>
      }
    >
      <div className="gap-phx-3 flex flex-col">
        <form.Field name="name">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('station.name.label')}
              required
              placeholder={t('station.name.placeholder')}
              data-testid="station-name"
            />
          )}
        </form.Field>

        <form.Field name="code">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('station.code.label')}
              placeholder="PHA-FLO"
            />
          )}
        </form.Field>

        <form.Field name="stationType">
          {(field) => (
            <StationTypeSelectorField
              field={field}
              mode="single"
              layout="inline"
              labelWidth={LABEL_W}
              label={t('station.type.label')}
              placeholder={t('station.type.placeholder')}
              testId="station-type"
            />
          )}
        </form.Field>

        <form.Field name="isDomestic">
          {(field) => (
            <Field layout="inline" labelWidth={LABEL_W} label={t('station.kind.label')}>
              <div className="border-phx-border-2 rounded-phx inline-flex w-fit overflow-hidden border">
                <button
                  type="button"
                  onClick={() => field.handleChange(true)}
                  className={cn(
                    'px-phx-3 py-phx-1 text-phx-sm',
                    field.state.value ? 'bg-phx-primary text-white' : 'bg-phx-surface text-phx-text'
                  )}
                >
                  {t('station.kind.in')}
                </button>
                <button
                  type="button"
                  onClick={() => field.handleChange(false)}
                  className={cn(
                    'px-phx-3 py-phx-1 text-phx-sm',
                    !field.state.value
                      ? 'bg-phx-primary text-white'
                      : 'bg-phx-surface text-phx-text'
                  )}
                >
                  {t('station.kind.out')}
                </button>
              </div>
            </Field>
          )}
        </form.Field>

        <form.Field name="destination">
          {(field) => (
            <DestinationSelectorField
              field={field}
              mode="single"
              layout="inline"
              labelWidth={LABEL_W}
              label={t('station.destination.label')}
              placeholder={t('station.destination.placeholder')}
              testId="station-destination"
            />
          )}
        </form.Field>

        <form.Field name="timeZoneOffset">
          {(field) => (
            <Field layout="inline" labelWidth={LABEL_W} label={t('station.timezone.label')}>
              <Select
                value={field.state.value ?? ''}
                onChange={(event) => field.handleChange(event.target.value || null)}
                placeholder={t('station.timezone.placeholder')}
                options={timeZones.map((option) => ({
                  value: option.id,
                  label: option.displayName,
                }))}
              />
            </Field>
          )}
        </form.Field>

        <form.Subscribe
          selector={(state) => ({
            details: state.values.stationDetails,
            lat: state.values.stationLatitude,
            lng: state.values.stationLongitude,
          })}
        >
          {({ details, lat, lng }) => {
            const hasPlaces = details.length > 0;
            const coord = hasPlaces ? details[0] : { latitude: lat, longitude: lng };
            const value =
              coord.latitude != null && coord.longitude != null
                ? `${coord.latitude.toFixed(4)}, ${coord.longitude.toFixed(4)}`
                : undefined;
            return (
              <InfoBar
                icon="pin"
                mono
                label={t('station.coords.label')}
                value={value}
                note={hasPlaces ? t('station.coords.fromFirst') : undefined}
                action={
                  hasPlaces ? undefined : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconLeft="pin"
                      onClick={onSetCoordsFromMap}
                    >
                      {lat != null
                        ? t('station.coords.changeInMap')
                        : t('station.coords.setFromMap')}
                    </Button>
                  )
                }
              />
            );
          }}
        </form.Subscribe>
      </div>
    </Panel>
  );
}
